import { Document, Font, Image, Page, Text, View } from "@react-pdf/renderer";
import React, { useMemo } from "react";
import robotoBold from "../../fonts/roboto-700.ttf";
import roboto from "../../fonts/roboto.ttf";
import { flattenObject } from "../../lib/flattenObject";
import { RentalPremierProduct, YesNo } from "../../lib/models/enums";
import { RentalCalculation } from "../../lib/models/rental/rental-calculation.model";
import {
  additionalCashRequirements,
  armLoanFeatureListItems,
  impounds,
  internalListItems,
  payments,
  payoffsLoanCosts,
  rateFeatures,
  rates,
  rentalIncome,
  rentalSummary,
  spread,
} from "../../lib/models/rental/rental-premier-term-sheet-map";
import {
  RentalPremierQuote,
  Scenario,
} from "../../lib/models/rental/rental.model";
import { formatMoney, toFixed } from "../../lib/number-transforms";
import { PdfGenerationActions } from "../../lib/types/PDFGenerationActions";
import pdfLogo from "../../limaone_logo.png";
import FixAndFlipTableSection from "./FixAndFlipTableSection";
import RentalTableSection from "./RentalTableSection";
import { styles } from "./styles";

export interface RentalPremierTermSheetProps {
  quote: RentalPremierQuote;
  calculation: RentalCalculation;
  scenarios: Scenario[];
  action: PdfGenerationActions;
}

function RentalPremierTermSheet({
  quote,
  calculation,
  scenarios,
  action,
}: RentalPremierTermSheetProps) {
  const quoteCalculation: { [key: string]: any } | { [key: number]: any } =
    useMemo(() => {
      if (quote && calculation && calculation?.scenarios) {
        for (let scenario of calculation.scenarios) {
          scenario.interestRateValue = scenario.premier?.interestRateValue || 0;
          scenario.prepaymentPenaltySelected = quote.prepaymentPenalty;
          scenario.openPrepayOptionSelected = quote.premier?.openPrepayOption;
          scenario.amortizationPeriod = `Term and Amortization Period: ${scenario.amortizationPeriod}`;

          if (scenario.premier) {
            scenario.interestRateName = scenario.premier.interestRateName;
            scenario.openPrepay = scenario.premier.openPrepay;
            scenario.index = scenario.premier.index;
            scenario.indexRate = scenario.premier.indexRate;
            scenario.dateRate = scenario.premier.dateRate;
            scenario.spread = scenario.premier.spread;
          }
        }

        return {
          ...calculation.scenarios,
        };
      } else {
        return {
          0: {},
          1: {},
          2: {},
        };
      }
    }, [calculation, quote]);

  const quoteDate = useMemo(() => {
    const date = new Date();
    return date.toLocaleDateString();
  }, []);

  const rentalScenarios = useMemo(() => {
    return scenarios.filter((x) => x.included === YesNo.Yes);
  }, [scenarios]);

  const rentalQuote: { [key: string]: any } = useMemo(() => {
    return flattenObject(quote) || {};
  }, [quote]);

  return (
    <Document>
      <Page size="LETTER" style={styles.body} wrap>
        <View style={styles.header}>
          <View style={styles.headerImageTitleContainer}>
            {action !== "WHITELABEL" ? (
              <View style={{ marginBottom: 15 }}>
                <Image
                  src={pdfLogo}
                  style={[styles.headerImage, { width: 120 }]}
                />
                <Text style={styles.addressText}>
                  300 East McBee Avenue, Suite 200 | Greenville, SC 29601
                </Text>
              </View>
            ) : null}

            <View>
              <Text
                style={[
                  styles.text,
                  styles.blueText,
                  { fontWeight: 700, marginBottom: 5, fontSize: 11 },
                ]}
              >
                Preliminary Estimate of Costs
              </Text>
              <Text
                style={[
                  styles.text,
                  styles.blueText,
                  { fontWeight: 700, marginBottom: 5, fontSize: 10 },
                ]}
              >
                Rental {quote.productType}
              </Text>
              <Text style={[styles.text, { fontSize: 9 }]}>{quoteDate}</Text>
            </View>
          </View>

          <View style={{ alignItems: "flex-end" }}>
            <View
              style={[
                styles.table,
                {
                  marginTop: 0,
                  marginLeft: -10,
                  maxWidth: "85%",
                  borderRadius: 2,
                  paddingHorizontal: 10,
                  paddingBottom: 5,
                  backgroundColor: "#f2f6fa",
                },
              ]}
            >
              <RentalTableSection
                label="Borrower Summary"
                scenarios={[rentalScenarios[0]]}
                section={rentalSummary}
                valueMap={quoteCalculation}
              />
            </View>
          </View>
        </View>

        <View style={[styles.table, { marginTop: -5 }]}>
          <View wrap={false}>
            <View
              style={[styles.tableRow, { borderWidth: 0, marginBottom: 2 }]}
            >
              <View style={[styles.tableCell, styles.tableCell2]}>
                <View
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Text style={[styles.textMd, { fontSize: 9 }]}>
                    Index:
                    {(quoteCalculation as { [key: number]: any })[0].index}
                    &nbsp; | &nbsp; Index Rate:
                    {toFixed(
                      +(quoteCalculation as { [key: number]: any })[0]
                        .indexRate * 100,
                      2
                    )}
                    %
                  </Text>
                  <Text
                    style={[styles.textMd, { fontWeight: 700, marginTop: 5 }]}
                  >
                    Loan Amount
                  </Text>
                </View>
              </View>

              {rentalScenarios.map((scenario, index) => (
                <View
                  key={scenario.scenarioId}
                  style={[
                    styles.tableCell,
                    styles.tableCell3,
                    { position: "relative", justifyContent: "flex-end" },
                  ]}
                >
                  {rentalScenarios.length > 2 && index % 2 !== 0 && (
                    <View
                      style={[styles.tableStripe, { height: "66vh" }]}
                    ></View>
                  )}

                  <View>
                    <Text
                      style={[
                        styles.text,
                        styles.blackText,
                        styles.textAlignRight,
                        { fontSize: 10, fontWeight: 700 },
                      ]}
                    >
                      {scenario.ltvPercentage}% LTV
                    </Text>
                    <Text
                      style={[
                        styles.text,
                        styles.blueText,
                        styles.textAlignRight,
                        { fontSize: 12, fontWeight: 700, marginTop: 5 },
                      ]}
                    >
                      ${" "}
                      {formatMoney(
                        (quoteCalculation as { [key: number]: any })[index]
                          .loanAmt,
                        0
                      )}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>

        <View>
          <View style={styles.table}>
            <RentalTableSection
              label="Payoffs and Loan Costs"
              scenarios={rentalScenarios}
              section={payoffsLoanCosts}
              valueMap={quoteCalculation}
            />

            <RentalTableSection
              label="Impounds"
              scenarios={rentalScenarios}
              section={impounds}
              valueMap={quoteCalculation}
            />

            <RentalTableSection
              label="Spread"
              scenarios={rentalScenarios}
              section={spread}
              valueMap={quoteCalculation}
            />

            <RentalTableSection
              label="Today's Indicated Rate; For Informational Purposes Only:"
              scenarios={rentalScenarios}
              section={rates}
              valueMap={quoteCalculation}
            />

            <RentalTableSection
              label="Monthly Payments"
              scenarios={rentalScenarios}
              section={payments}
              valueMap={quoteCalculation}
            />

            <RentalTableSection
              label="Rental Income"
              scenarios={rentalScenarios}
              section={rentalIncome}
              valueMap={quoteCalculation}
            />
          </View>
        </View>

        {quote.productType === RentalPremierProduct["Premier - 5/1 ARM"] && (
          <View style={{ marginTop: 10 }}>
            <Text
              style={[styles.textSmBlock, styles.blueText, { marginBottom: 4 }]}
            >
              Additional Information
            </Text>
            <View>
              <View>
                <Text style={[styles.textSm, { fontSize: 7 }]}>
                  1. Cash to close figures include an estimation of 3rd party
                  Closing Costs, which will be charged by your selected title
                  company.
                </Text>
              </View>
              <View style={{ marginTop: 5 }}>
                <Text style={[styles.textSm, { fontSize: 7 }]}>
                  2. The annual fixed Interest Rate for the Loan will be equal
                  to the sum of (i) the Spread and (ii) the greater of the 10
                  Year Swap Rate as of the date 5 days prior to the Closing Date
                  and 0.70%. Based on the referenced 10 Year Swap Rate as of the
                  date of your quote, Today's indicative rate is presented below
                  for information purposes only. The Interest Rate is subject to
                  change if there are material changes in prevailing interest
                  rates or Borrower's application, such as, but not limited to
                  changes in the loan amount, program type, loan to value or
                  other changed circumstances.
                </Text>
              </View>
            </View>
          </View>
        )}

        <View style={styles.footer} fixed>
          <Text
            style={[styles.textSmBlock, styles.blueText]}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${
                action === "INTERNAL" ? totalPages - 1 : totalPages
              }`
            }
          />
        </View>
      </Page>

      <Page size="LETTER" style={styles.body}>
        <View style={styles.header}>
          {action !== "WHITELABEL" ? (
            <View>
              <Image src={pdfLogo} style={styles.headerImage} />
              <Text style={styles.addressText}>
                300 East McBee Avenue, Suite 200 | Greenville, SC 29601
              </Text>
            </View>
          ) : (
            <View></View>
          )}
          <View style={{ alignItems: "flex-end" }}>
            <Text
              style={[
                styles.text,
                styles.blueText,
                { fontWeight: 700, marginBottom: 5, fontSize: 10 },
              ]}
            >
              Preliminary Estimate of Costs
            </Text>
            <Text
              style={[
                styles.text,
                styles.blueText,
                { fontWeight: 700, marginBottom: 5, fontSize: 9 },
              ]}
            >
              Rental {quote.productType}
            </Text>
            <Text style={[styles.text, { fontSize: 8 }]}>{quoteDate}</Text>
          </View>
        </View>

        <View
          style={{ paddingBottom: 15, borderBottom: "1px solid #E2E8F0" }}
          wrap={false}
        >
          <Text
            style={[
              styles.textLg,
              styles.blueText,
              { marginTop: 15, fontWeight: 700 },
            ]}
          >
            Disclosures and Contingencies
          </Text>

          {quote.productType === RentalPremierProduct["Premier - 5/1 ARM"] ? (
            <RentalTableSection
              label="Rate Features"
              scenarios={rentalScenarios}
              section={rateFeatures}
              valueMap={quoteCalculation}
              rowStyling={styles.tableRowNoBorder}
            />
          ) : null}

          <View style={{ marginTop: 15 }}>
            <Text
              style={[styles.textSmBlock, styles.blueText, { marginBottom: 4 }]}
            >
              Interest Reserve Account
            </Text>
            <Text style={styles.textSm}>
              So long as no Event of Default has occurred and is continuing and
              no event with the passage of time and/or the giving of notice
              would constitute a default hereunder or under any other Loan
              Documents have occurred. Lender shall credit, the Interest Reserve
              towards the then due interest, principal, tax, and insurance
              payment under the Note.
            </Text>
          </View>

          <View>
            <RentalTableSection
              label="Additional Cash Requirements"
              scenarios={rentalScenarios}
              section={additionalCashRequirements}
              valueMap={quoteCalculation}
              rowStyling={styles.tableRowNoBorder}
            />
          </View>

          <View style={{ marginTop: 15 }}>
            <Text
              style={[styles.textSmBlock, styles.blueText, { marginBottom: 4 }]}
            >
              Prepayment Penalty
            </Text>
            <Text style={styles.textSm}>
              {(quoteCalculation as { [key: number]: any })[0][
                "prepaymentPenaltyText"
              ]
                .split("•")
                .filter((x: string) => x)
                .map((x: string) => x.trim())
                .join(" ")}
            </Text>
          </View>

          <View style={{ marginTop: 15 }}>
            <Text
              style={[styles.textSmBlock, styles.blueText, { marginBottom: 4 }]}
            >
              Open Prepay Option
            </Text>
            <Text style={styles.textSm}>
              Prepay penalty will be applied to any prepayments of the scheduled
              principal balance during the prepayment period.
            </Text>
          </View>

          {quote.productType === RentalPremierProduct["Premier - 5/1 ARM"] ? (
            <View style={{ marginTop: 15 }}>
              <Text
                style={[
                  styles.textSmBlock,
                  styles.blueText,
                  { marginBottom: 4 },
                ]}
              >
                {armLoanFeatureListItems.label}
              </Text>
              {armLoanFeatureListItems.items.map((x, index) => (
                <View
                  key={index}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginTop: 4,
                  }}
                >
                  <Text style={styles.textSm}>-</Text>
                  <Text style={[styles.textSm, { flex: 1, marginLeft: 4 }]}>
                    {x}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}

          <View style={{ marginTop: 15 }}>
            <Text
              style={[styles.textSmBlock, styles.blueText, { marginBottom: 4 }]}
            >
              Release Price
            </Text>
            <Text style={styles.textSm}>
              The lien against an individual property will be released when 115%
              of the original loan amount allocated to that property is paid in
              full.
            </Text>
          </View>

          <View style={{ marginTop: 15 }}>
            <Text
              style={[styles.textSmBlock, styles.blueText, { marginBottom: 4 }]}
            >
              Personal Guaranty
            </Text>
            <Text style={styles.textSm}>
              All loans must be personally guaranteed by all members of the
              borrowing entity.
            </Text>
          </View>

          <View style={{ marginTop: 15 }}>
            <Text
              style={[styles.textSmBlock, styles.blueText, { marginBottom: 4 }]}
            >
              Preliminary Estimate is Subject to Change
            </Text>
            <Text style={styles.textSm}>
              This estimate of costs is contingent upon full borrower and
              property underwriting.
            </Text>
          </View>

          <View style={{ marginTop: 15 }}>
            <Text
              style={[styles.textSmBlock, styles.blueText, { marginBottom: 4 }]}
            >
              Business purpose
            </Text>
            <Text style={styles.textSm}>
              The Rental30 Loan is for business purpose use only, specifically,
              for real estate investors purchasing, refinancing, or cashing-out
              of non-owner occupied, rental properties.
            </Text>
          </View>
        </View>

        {quote.productType !== RentalPremierProduct["Premier - 5/1 ARM"] && (
          <View style={{ marginTop: 10 }}>
            <Text
              style={[styles.textSmBlock, styles.blueText, { marginBottom: 4 }]}
            >
              Additional Information
            </Text>
            <View>
              <View>
                <Text style={styles.textSm}>
                  1. For Information Purposes Only - The above cash to close
                  figures do not include 3rd party title fees.
                </Text>
              </View>
              <View style={{ marginTop: 10 }}>
                {quote.productType ===
                  RentalPremierProduct["Premier - 5 Year, 30 Am"] ||
                quote.productType ===
                  RentalPremierProduct["Premier - 5 Year Balloon"] ? (
                  <Text style={styles.textSm}>
                    2. The annual fixed Interest Rate for the Loan will be equal
                    to the sum of (i) the Spread and (ii) the greater of the 5
                    Year Swap Rate as of the date 5 days prior to the Closing
                    Date and 0.50%. Based on the referenced 5 Year Swap Rate as
                    of the date of your quote, Today's indicative rate is
                    presented below for information purposes only. The Interest
                    Rate is subject to change if there are material changes in
                    prevailing interest rates or Borrower's application, such
                    as, but not limited to changes in the loan amount, program
                    type, loan to value or other changed circumstances.
                  </Text>
                ) : (
                  <Text style={styles.textSm}>
                    2. The annual fixed Interest Rate for the Loan will be equal
                    to the sum of (i) the Spread and (ii) the greater of the 10
                    Year Swap Rate as of the date 5 days prior to the Closing
                    Date and 0.70%. Based on the referenced 10 Year Swap Rate as
                    of the date of your quote, Today's indicative rate is
                    presented below for information purposes only. The Interest
                    Rate is subject to change if there are material changes in
                    prevailing interest rates or Borrower's application, such
                    as, but not limited to changes in the loan amount, program
                    type, loan to value or other changed circumstances.
                  </Text>
                )}
              </View>
            </View>
          </View>
        )}

        <View style={styles.footer} fixed>
          {action !== "WHITELABEL" ? (
            <Text
              style={[
                styles.textSm,
                styles.grayText,
                styles.textAlignCenter,
                { marginBottom: 20, fontSize: 7, marginHorizontal: "5.75%" },
              ]}
            >
              This estimate is for informational purposes only and does not
              constitute loan commitment or a rate lock. All rates and terms may
              change without notice and are subject to credit approval,
              underwriting, and verification of the information provided. Final
              terms and conditions will be detailed in definitive loan
              documents. Lima One is an equal housing lender. NMLS ID # 1324403
              (www.nmlsconsumeraccess.org).
            </Text>
          ) : (
            <Text
              style={[
                styles.textSm,
                styles.grayText,
                styles.textAlignCenter,
                { marginBottom: 20, fontSize: 7, marginHorizontal: "5.75%" },
              ]}
            >
              This estimate is for informational purposes only and is not a
              commitment to lend. All loans are subject to borrower
              qualification and final credit approval. Approvals are subject to
              underwriting and program guidelines, and are subject to change
              without notice in the lender’s sole discretion.
            </Text>
          )}

          <Text
            style={[styles.textSmBlock, styles.blueText]}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${
                action === "INTERNAL" ? totalPages - 1 : totalPages
              }`
            }
          />
        </View>
      </Page>

      {action === "INTERNAL" && (
        <Page size="LETTER" style={styles.body}>
          <View style={styles.header} fixed>
            <View>
              <Image src={pdfLogo} style={styles.headerImage} />
              <Text style={styles.addressText}>
                300 East McBee Avenue, Suite 200 | Greenville, SC 29601
              </Text>
            </View>

            <View style={{ alignItems: "flex-end" }}>
              <Text
                style={[
                  styles.text,
                  styles.blueText,
                  { fontWeight: 700, marginBottom: 5, fontSize: 10 },
                ]}
              >
                Internal Selections
              </Text>
              <Text
                style={[
                  styles.text,
                  styles.blueText,
                  { fontWeight: 700, marginBottom: 5, fontSize: 9 },
                ]}
              >
                Rental {quote.productType}
              </Text>
              <Text style={[styles.text, { fontSize: 8 }]}>{quoteDate}</Text>
            </View>
          </View>

          <View style={styles.table}>
            <FixAndFlipTableSection
              label="Internal Selections"
              section={internalListItems}
              valueMap={rentalQuote}
            />
          </View>
        </Page>
      )}
    </Document>
  );
}

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: roboto,
    },
    {
      src: robotoBold,
      fontWeight: 700,
    },
  ],
});

export default RentalPremierTermSheet;
